import type { ReactNode } from "react";
import { Alert } from "rizzui";
import { PiWarningDuotone as AlertTriangle, PiCloudWarningDuotone as ServerCrash, PiShieldWarningDuotone as ShieldOff } from "react-icons/pi";
import { isProblemDetails } from "./problem-details";

/**
 * 018: moved here from `apps/admin/src/components/`. It is rule-bearing, not chrome — the single
 * place a failed read or write is reported (constitution VI), with the problem-document vocabulary
 * that goes with it — so it belongs beside the other domain components rather than in an app's
 * components folder, which now holds only copied template files and data wiring.
 *
 * Ported from packages/ui/src/error-surface.tsx (business logic) rebuilt on rizzui's `Alert`
 * (presentation) rather than the earlier hand-rolled card — this is the one place this app
 * reports a failed read or write (constitution VI). No generic-message prop, an unrecognised
 * error still renders its own shape.
 */
function iconFor(status: number | undefined) {
  if (status === 401 || status === 403) return <ShieldOff aria-hidden className="size-5" />;
  if (status !== undefined && status >= 500) return <ServerCrash aria-hidden className="size-5" />;
  return <AlertTriangle aria-hidden className="size-5" />;
}

function colorFor(status: number | undefined): "danger" | "warning" {
  return status === 401 || status === 403 ? "warning" : "danger";
}

export function ErrorSurface({
  error,
  action,
  children,
}: {
  error: unknown;
  action: string;
  children?: ReactNode;
}) {
  const problem = isProblemDetails(error) ? error : null;
  const status = problem?.status;

  return (
    <Alert color={colorFor(status)} variant="flat" icon={iconFor(status)}>
      <div role="alert" className="flex min-w-0 flex-col gap-1">
        <p className="font-semibold">Could not {action}.</p>
        {problem ? (
          <>
            {problem.title ? <p>{problem.title}</p> : null}
            {problem.detail ? <p className="text-muted-foreground">{problem.detail}</p> : null}
            {status ? (
              <p className="mt-1 text-xs text-muted-foreground">
                <span className="mr-1.5 rounded border border-muted px-1.5 py-0.5 font-mono text-foreground">{status}</span>
                {status >= 502 && status <= 504
                  ? "the service could not be reached; this may be temporary, and trying again may work"
                  : status >= 500
                    ? "the service reported a problem with its own data; re-sending will not fix it"
                    : "the request was rejected"}
              </p>
            ) : null}
          </>
        ) : (
          <p className="text-muted-foreground">{error instanceof Error ? error.message : String(error)}</p>
        )}
        {children ? <div className="mt-2">{children}</div> : null}
      </div>
    </Alert>
  );
}
